import { supabase } from './supabaseClient';

/* Families, versions and batches.
 *
 * A "formula" row is ONE IMMUTABLE VERSION of a family. Editing an
 * established formula creates a new version rather than rewriting an old
 * one, because batches reference the exact version they were made from —
 * mutating a version in place would retroactively rewrite production
 * history. The legacy addFormula/updateFormula below still exist for the
 * old single-row flow and are being phased out. */

const VERSION_SELECT = '*, formula_ingredients(*, ingredient:ingredients(*))';

export async function listFamilies() {
  const { data, error } = await supabase
    .from('formula_families')
    .select(`*, base:base_family_id(id, name, product_type), versions:formulas(${VERSION_SELECT})`)
    .order('name');
  if (error) throw error;
  return data.map((family) => ({
    ...family,
    versions: [...(family.versions || [])].sort((a, b) => b.version_number - a.version_number),
    currentVersion: (family.versions || []).find((v) => v.is_current) || null,
  }));
}

export const FORMULA_IMAGE_BUCKET = 'formula-images';

export function formulaImageUrl(path) {
  if (!path) return null;
  return supabase.storage.from(FORMULA_IMAGE_BUCKET).getPublicUrl(path).data.publicUrl;
}

// The photo belongs to the product, not to a version — creating a new
// version keeps the same image rather than asking for it again.
export async function uploadFamilyImage(familyId, file) {
  const ext = file.name.split('.').pop();
  const path = `${familyId}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from(FORMULA_IMAGE_BUCKET).upload(path, file);
  if (error) throw error;
  await updateFamily(familyId, { image_path: path });
  return path;
}

export async function createFamily(fields) {
  const { data, error } = await supabase.from('formula_families').insert(fields).select().single();
  if (error) throw error;
  return data;
}

export async function updateFamily(id, updates) {
  const { data, error } = await supabase
    .from('formula_families')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteFamily(id) {
  const { error } = await supabase.from('formula_families').delete().eq('id', id);
  if (error) throw error;
}

// Creates the next immutable version of a family and makes it current.
// The previous current version is demoted FIRST: a partial unique index
// allows only one `is_current` row per family, so inserting before demoting
// would be rejected.
// Creates a family and its first version together — the normal "add a new
// formula" path, since a family with no version has nothing to show.
export async function createFamilyWithVersion(familyFields, versionFields, ingredients) {
  const family = await createFamily(familyFields);
  await createVersion(family.id, versionFields, ingredients, 'Initial version');
  return family;
}

export async function createVersion(familyId, fields, ingredients, changeNote) {
  const { data: existing, error: readError } = await supabase
    .from('formulas')
    .select('version_number')
    .eq('family_id', familyId)
    .order('version_number', { ascending: false })
    .limit(1);
  if (readError) throw readError;

  const nextNumber = (existing?.[0]?.version_number || 0) + 1;

  const { error: demoteError } = await supabase
    .from('formulas')
    .update({ is_current: false })
    .eq('family_id', familyId)
    .eq('is_current', true);
  if (demoteError) throw demoteError;

  const { data: version, error: insertError } = await supabase
    .from('formulas')
    .insert({
      ...fields,
      family_id: familyId,
      version_number: nextNumber,
      version_label: `v${nextNumber}`,
      is_current: true,
      change_note: changeNote || null,
    })
    .select()
    .single();
  if (insertError) throw insertError;

  await insertVersionIngredients(version.id, ingredients);
  await supabase
    .from('formula_families')
    .update({ updated_at: new Date().toISOString() })
    .eq('id', familyId);

  return version;
}

async function insertVersionIngredients(formulaId, ingredients) {
  if (!ingredients?.length) return;
  const rows = ingredients.map((line, index) => ({
    formula_id: formulaId,
    sort_order: index,
    ingredient_id: line.ingredient_id || null,
    ingredient_name: line.ingredient_name,
    inci_name: line.inci_name || null,
    percentage: line.percentage,
    cost_per_kg: line.cost_per_kg ?? null,
    phase: line.phase || null,
    notes: line.notes || null,
  }));
  const { error } = await supabase.from('formula_ingredients').insert(rows);
  if (error) throw error;
}

// Makes an older version current again without destroying anything — the
// newer versions stay in history.
export async function setCurrentVersion(familyId, versionId) {
  const { error: demoteError } = await supabase
    .from('formulas')
    .update({ is_current: false })
    .eq('family_id', familyId)
    .eq('is_current', true);
  if (demoteError) throw demoteError;

  const { error } = await supabase
    .from('formulas')
    .update({ is_current: true })
    .eq('id', versionId);
  if (error) throw error;
}

export const PRODUCT_TYPES = ['Body Cream', 'Body Scrub', 'Soap Bar', 'Lip Balm', 'Body Oil', 'Other'];

export const CONTAINER_SIZES_OZ = [5, 6, 8];

// Weight ounce, not fluid ounce — matches how the existing formula sheet
// sizes jars (e.g. 15 x 8oz jars = 3,402g, i.e. 8 x 28.3495 x 15).
const GRAMS_PER_OZ = 28.3495;

export function containerFillToGrams(containerOz, containerCount) {
  return containerOz * containerCount * GRAMS_PER_OZ;
}

export async function listFormulas() {
  const { data, error } = await supabase
    .from('formulas')
    .select('*, formula_ingredients(*)')
    .order('product_name')
    .order('created_at')
    .order('sort_order', { foreignTable: 'formula_ingredients' });
  if (error) throw error;
  return data;
}

export async function addFormula(formula, ingredients) {
  const { data: formulaRow, error: formulaError } = await supabase
    .from('formulas')
    .insert(formula)
    .select()
    .single();
  if (formulaError) throw formulaError;

  await insertIngredients(formulaRow.id, ingredients);
  return formulaRow;
}

export async function updateFormula(id, formula, ingredients) {
  const { error: formulaError } = await supabase.from('formulas').update(formula).eq('id', id);
  if (formulaError) throw formulaError;

  const { error: deleteError } = await supabase
    .from('formula_ingredients')
    .delete()
    .eq('formula_id', id);
  if (deleteError) throw deleteError;

  await insertIngredients(id, ingredients);
}

async function insertIngredients(formulaId, ingredients) {
  const rows = ingredients.map((ingredient, index) => ({
    formula_id: formulaId,
    sort_order: index,
    ingredient_name: ingredient.ingredient_name,
    inci_name: ingredient.inci_name || null,
    percentage: ingredient.percentage,
    cost_per_kg: ingredient.cost_per_kg ?? null,
  }));
  const { error } = await supabase.from('formula_ingredients').insert(rows);
  if (error) throw error;
}

export async function deleteFormula(id) {
  const { error } = await supabase.from('formulas').delete().eq('id', id);
  if (error) throw error;
}

export function sumPercentage(ingredients) {
  return ingredients.reduce((sum, i) => sum + Number(i.percentage || 0), 0);
}

export function ingredientCost(ingredient, batchSizeG) {
  const grams = (batchSizeG * Number(ingredient.percentage || 0)) / 100;
  return (grams / 1000) * Number(ingredient.cost_per_kg || 0);
}

export function totalBatchCost(ingredients, batchSizeG) {
  return ingredients.reduce((sum, i) => sum + ingredientCost(i, batchSizeG), 0);
}

export function costPerUnit(totalCost, yieldUnits) {
  if (!yieldUnits) return null;
  return totalCost / yieldUnits;
}

// Lightweight formula list for pickers (the Production Log's formula
// dropdown) — skips the nested ingredients listFormulas() pulls in, since
// only id/name/version/type are needed here.
export async function listFormulaOptions() {
  const { data, error } = await supabase
    .from('formulas')
    .select('id, product_name, version_label, product_type')
    .order('product_name')
    .order('created_at');
  if (error) throw error;
  return data;
}

export async function listAllBatches() {
  const { data, error } = await supabase
    .from('formula_batches')
    // The FK must be named: formula_batches now has TWO references to
    // formulas (formula_id and base_formula_id), so a bare
    // `formula:formulas(...)` is ambiguous and PostgREST refuses the embed.
    .select(
      '*, formula:formulas!formula_batches_formula_id_fkey(id, product_name, version_label, version_number, family:formula_families(id, name))'
    )
    .order('made_on', { ascending: false });
  if (error) throw error;
  return data;
}

// Batches for one family, newest first — the Batch History tab in the
// formula workspace. Production Log remains the primary place records live;
// this is a read-only view of the same rows.
export async function listBatchesForFamily(familyId) {
  const { data, error } = await supabase
    .from('formula_batches')
    .select('*, formula:formulas!formula_batches_formula_id_fkey!inner(id, version_number, family_id)')
    .eq('formula.family_id', familyId)
    .order('made_on', { ascending: false });
  if (error) throw error;
  return data;
}

export async function addBatch(batch) {
  const { data, error } = await supabase.from('formula_batches').insert(batch).select().single();
  if (error) throw error;
  return data;
}

export async function updateBatch(id, updates) {
  const { data, error } = await supabase
    .from('formula_batches')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteBatch(id) {
  const { error } = await supabase.from('formula_batches').delete().eq('id', id);
  if (error) throw error;
}
